<template>
    <div class="ai-dict">
        <div v-if="!state" class="ai-dict-mic" title="Dictate instead of typing" @click="start()">
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/></svg>
        </div>
        <div v-else class="ai-dict-pill">
            <div class="ai-dict-btn ai-dict-cancel" title="Cancel" @click="cancel()">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/></svg>
            </div>
            <div class="ai-dict-wave">
                <span v-for="n in 9" :key="n" :style="{ animationDelay: (n * 0.11) + 's' }" :class="state == 'converting' ? 'ai-dict-bar-idle' : ''"></span>
            </div>
            <div class="ai-dict-btn ai-dict-ok" :title="state == 'converting' ? 'Converting...' : 'Stop and convert to text'" @click="convert()">
                <svg v-if="state == 'recording'" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                <span v-else class="ai-dict-spin"></span>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

export default defineComponent({
    name: 'AIDictation',
    emits: ['text'],
    data() {
        return {
            state: null as any, // null | recording | converting
            recorder: null as any,
            chunks: [] as any[],
            cancelled: false
        }
    },
    unmounted() {
        this.teardown();
    },
    methods: {
        async start() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.chunks = [];
                this.cancelled = false;
                this.recorder = new MediaRecorder(stream);
                this.recorder.ondataavailable = (e: any) => { if (e.data.size) this.chunks.push(e.data); };
                this.recorder.onstop = () => this.onStopped();
                this.recorder.start();
                this.state = 'recording';
            } catch (e) {
                alert('Microphone access was blocked — allow it in your browser to dictate.');
            }
        },
        cancel() {
            this.cancelled = true;
            if (this.recorder && this.recorder.state != 'inactive') this.recorder.stop();
            else this.teardown();
        },
        convert() {
            if (this.state != 'recording') return;
            this.state = 'converting';
            this.recorder.stop();
        },
        async onStopped() {
            const stream = this.recorder?.stream;
            if (stream) stream.getTracks().forEach((t: any) => t.stop());
            if (this.cancelled) { this.teardown(); return; }
            const blob = new Blob(this.chunks, { type: this.recorder.mimeType || 'audio/webm' });
            const formData = new FormData();
            formData.append('audio', blob, 'dictation.webm');
            await axios.post(`${this.$store.state.apiUrl}/api/ai/transcribe`, formData, { timeout: 300000 })
                .then(resp => {
                    if (resp.data.text) this.$emit('text', resp.data.text);
                })
                .catch(e => {
                    alert(e?.response?.data || 'Could not convert the recording — please try again.');
                });
            this.teardown();
        },
        teardown() {
            const stream = this.recorder?.stream;
            if (stream) stream.getTracks().forEach((t: any) => t.stop());
            this.state = null;
            this.recorder = null;
            this.chunks = [];
        }
    }
});
</script>

<style scoped>
.ai-dict-mic {
    width: 30px; height: 30px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: var(--ai-accent); background: #fff4ea; border: 1px solid var(--ai-border-soft);
    cursor: pointer; transition: all .2s ease;
}
.ai-dict-mic:hover { background: #ffedd5; }
.ai-dict-pill {
    display: flex; align-items: center; gap: 10px;
    background: rgba(30, 20, 12, .93); border-radius: 26px; padding: 6px;
    box-shadow: 0 8px 28px rgba(30, 20, 12, .45);
}
.ai-dict-btn {
    width: 36px; height: 36px; border-radius: 50%; cursor: pointer;
    display: flex; align-items: center; justify-content: center; transition: all .2s ease;
}
.ai-dict-cancel { background: rgba(255, 255, 255, .18); color: #fff; }
.ai-dict-cancel:hover { background: rgba(255, 255, 255, .3); }
.ai-dict-ok { background: var(--ai-grad); color: #fff; box-shadow: 0 2px 10px rgba(234, 88, 12, .5); }
.ai-dict-ok:hover { transform: scale(1.06); }
.ai-dict-wave { display: flex; align-items: center; gap: 3px; height: 24px; padding: 0 4px; }
.ai-dict-wave span {
    width: 3px; height: 6px; border-radius: 2px; background: #fff;
    animation: ai-dict-bounce 1s ease-in-out infinite;
}
.ai-dict-wave .ai-dict-bar-idle { animation-play-state: paused; opacity: .4; }
@keyframes ai-dict-bounce {
    0%, 100% { height: 5px; opacity: .55; }
    50% { height: 20px; opacity: 1; }
}
.ai-dict-spin {
    width: 15px; height: 15px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,.35); border-top-color: #fff;
    animation: ai-dict-rot .8s linear infinite;
}
@keyframes ai-dict-rot { to { transform: rotate(360deg); } }
</style>
